/**
 * Ürün listesi Trendyol sütunu: link sırası ve rozet mantığı (Firestore alanları eksik olsa bile güvenli).
 */

/** Trendyol'da «satışta» kabul edilen ty_listingStatus değerleri (küçük harf normalize). */
const TY_LISTING_SALE_STATUSES = new Set([
  'on_sale',
  'approved',
  'active',
  'live',
  'published',
  'listed'
]);

/** Onay sürecinde / batch beklerken — liste satırından durum yenilenebilir. */
const TY_LISTING_PENDING_STATUSES = new Set([
  'pending_batch',
  'pending_approval',
  'waiting',
  'waiting_for_approval',
  'in_review'
]);

const trim = (v) => String(v ?? '').trim();
const lower = (v) => trim(v).toLowerCase();

/** @param {Record<string, unknown>} doc */
export function isTyListingPendingStatus(doc) {
  const ls = lower(doc?.ty_listingStatus);
  const syncSt = lower(doc?.trendyolSyncStatus);
  if (syncSt === 'pending_batch') return true;
  if (!ls || TY_LISTING_SALE_STATUSES.has(ls)) return false;
  if (TY_LISTING_PENDING_STATUSES.has(ls)) return true;
  return ls.includes('pending') || ls.includes('waiting') || ls.includes('approval');
}

/** @param {Record<string, unknown>} doc */
export function pickTrendyolProductUrl(doc) {
  if (!doc || typeof doc !== 'object') return '';
  const keys = ['ty_productUrl', 'ty_matchedProductUrl', 'ty_listingUrl', 'ty_matchedUrl'];
  for (const k of keys) {
    const u = String(doc[k] ?? '').trim();
    if (u && /^https?:\/\//i.test(u)) return u;
  }
  return '';
}

/** ty_listingStatus satışta grubunda mı */
export function isTyListingSaleStatus(doc) {
  const ls = lower(doc?.ty_listingStatus);
  if (!ls) return false;
  return TY_LISTING_SALE_STATUSES.has(ls);
}

const chipSxReadable = (colors) => ({
  ...colors,
  height: 'auto',
  minHeight: 22,
  minWidth: 'fit-content',
  maxWidth: 'none',
  width: 'auto',
  fontSize: '0.65rem',
  fontWeight: 800,
  '& .MuiChip-label': {
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    px: 0.85,
    py: 0.25,
    lineHeight: 1.25
  }
});

/**
 * @returns {{ label: string, chipColor?: 'success' | 'primary' | 'warning' | 'error', sx?: object, tooltip: string }}
 */
export function deriveTrendyolProductListBadge(doc) {
  const d = doc && typeof doc === 'object' ? doc : {};

  const tips = [];

  const syncErr = lower(d.trendyolSyncStatus) === 'error';
  const lastErr = trim(d.trendyolLastError);
  const listingErr = lower(d.ty_listingStatus) === 'status_error';
  const lastStatusErr = trim(d.ty_lastStatusError);
  const anyErrMsg = lastErr || lastStatusErr;

  tips.push(`trendyolSyncStatus: ${trim(d.trendyolSyncStatus) || '—'}`);
  if (trim(d.ty_listingStatus)) tips.push(`ty_listingStatus: ${trim(d.ty_listingStatus)}`);
  const url = pickTrendyolProductUrl(d);
  if (url) tips.push(`Link: ${url}`);
  if (lastErr) tips.push(`Trendyol hata: ${lastErr}`);
  if (lastStatusErr) tips.push(`Durum sorgu hata: ${lastStatusErr}`);
  const tooltip = tips.join('\n');

  const graySx = chipSxReadable({
    bgcolor: '#f1f5f9',
    color: '#64748b'
  });

  const listingBlocked =
    lower(d.ty_listingStatus) === 'rejected' || lower(d.ty_listingStatus) === 'blacklisted';

  if (syncErr || anyErrMsg || listingErr || listingBlocked) {
    return {
      label: 'Hata',
      chipColor: 'error',
      tooltip,
      sx: chipSxReadable({})
    };
  }

  /**
   * ty_listingStatus satıştaysa pending_batch’i ezer (refresh sonrası gerçek durum).
   * Öncelik: link + satış statüsü; statü tek başına da (TY onayı sonrası ürün satışta) yeterli.
   */
  const saleByListing = isTyListingSaleStatus(d);
  if (saleByListing) {
    return {
      label: 'Satışta',
      chipColor: 'success',
      tooltip,
      sx: chipSxReadable({})
    };
  }

  if (isTyListingPendingStatus(d)) {
    return {
      label: 'Onayda',
      tooltip,
      sx: chipSxReadable({
        bgcolor: '#e2e8f0',
        color: '#334155'
      })
    };
  }

  const matchedMode = lower(d.ty_syncMode) === 'matched';
  const skippedMatched = lower(d.trendyolSyncStatus) === 'skipped_matched_listing';

  if (matchedMode || skippedMatched) {
    return {
      label: 'Eşleşti',
      tooltip,
      sx: chipSxReadable({
        bgcolor: '#ffedd5',
        color: '#c2410c',
        border: '1px solid #fdba74'
      })
    };
  }

  const shallow =
    trim(d.trendyolSyncStatus) ||
    trim(d.ty_listingStatus) ||
    trim(d.ty_syncMode) ||
    trim(d.trendyolBatchRequestId);
  const hasMatchedMeta = !!(trim(d.ty_matchedProductId) || trim(d.ty_matchedProductUrl));
  const hasNeutralInfo = !!(url || shallow || hasMatchedMeta);

  if (hasNeutralInfo) {
    return { label: 'Yok', tooltip, sx: graySx };
  }

  return {
    label: 'Yok',
    tooltip: 'Trendyol entegrasyon alanı kayıtta yok',
    sx: graySx
  };
}

/**
 * Ürün listesi tablosunda tek Trendyol düğümü:
 * - `sale`: satışta (yeşil, link aç).
 * - `pending`: onay sürecinde (gri; tıklayınca sunucuya durum sorgusu).
 * - `error`: hata (kırmızı, devre dışı).
 * - `closed`: satış kapalı veya bağlantı yok (kırmızı, bağlantı yoksa pasif).
 * @returns {{ tableVariant: 'sale'|'pending'|'error'|'closed', label: string, greenLook: boolean, disabled: boolean, href: string, tooltip: string, isPendingRefresh: boolean }}
 */
export function computeTrendyolMarketplaceTableButton(doc) {
  const d = doc && typeof doc === 'object' ? doc : {};

  const tyUrl = pickTrendyolProductUrl(d);

  const listingBlocked =
    lower(d.ty_listingStatus) === 'rejected' ||
    lower(d.ty_listingStatus) === 'blacklisted';

  const tyErr =
    lower(d.trendyolSyncStatus) === 'error' ||
    Boolean(trim(d.trendyolLastError)) ||
    lower(d.ty_listingStatus) === 'status_error' ||
    Boolean(trim(d.ty_lastStatusError)) ||
    listingBlocked;

  if (tyErr) {
    let detail = trim(d.trendyolLastError) || trim(d.ty_lastStatusError);
    if (!detail && listingBlocked) {
      const st = trim(d.ty_listingStatus);
      detail = `İlan reddedilmiş veya bloklu (${st || '-'})`;
    }
    if (!detail) detail = 'Trendyol kayıt hatası';
    const tip = [`${detail}`, tyUrl ? `Bağlantı: ${tyUrl}` : 'Ürün bağlantısı yok'].join('\n');
    return {
      tableVariant: 'error',
      label: 'Hata',
      greenLook: false,
      disabled: true,
      href: tyUrl || '',
      tooltip: tip,
      isPendingRefresh: false
    };
  }

  const matchedOk = lower(d.ty_syncMode) === 'matched';
  const saleOk = isTyListingSaleStatus(d);
  const primUrl =
    /^https?:\/\//i.test(trim(d.ty_productUrl)) ||
    /^https?:\/\//i.test(trim(d.ty_matchedProductUrl));

  const tooltipLinesBase = [];
  if (trim(d.ty_listingStatus)) tooltipLinesBase.push(`ty_listingStatus: ${trim(d.ty_listingStatus)}`);
  if (trim(d.ty_syncMode)) tooltipLinesBase.push(`ty_syncMode: ${trim(d.ty_syncMode)}`);

  /** Satışta işaretli ise yeşil; link yoksa gri «Onayda» yenile bekler */
  if (saleOk) {
    const hrefOut =
      tyUrl ||
      (primUrl ? trim(d.ty_productUrl) || trim(d.ty_matchedProductUrl) : '');
    tooltipLinesBase.push(hrefOut ? `Bağlantı: ${hrefOut}` : 'Trendyol ürün bağlantısı yok — yenile ile güncellenir');
    if (hrefOut) {
      return {
        tableVariant: 'sale',
        label: 'Trendyol',
        greenLook: true,
        disabled: false,
        href: hrefOut,
        tooltip: tooltipLinesBase.join('\n'),
        isPendingRefresh: false
      };
    }
    tooltipLinesBase.push('Onay sürecinde veya bağlantı eksik. Tıklayınca Trendyol’daki güncel durum alınır (Firestore yazılır).');
    return {
      tableVariant: 'pending',
      label: 'Onayda',
      greenLook: false,
      disabled: false,
      href: '',
      tooltip: tooltipLinesBase.join('\n'),
      isPendingRefresh: true
    };
  }

  const matchedGreen =
    Boolean(tyUrl || primUrl) &&
    matchedOk &&
    !isTyListingPendingStatus(d);

  if (matchedGreen) {
    const hrefOut = tyUrl || trim(d.ty_productUrl) || trim(d.ty_matchedProductUrl);
    tooltipLinesBase.push(`Bağlantı: ${hrefOut}`);
    return {
      tableVariant: 'sale',
      label: 'Trendyol',
      greenLook: true,
      disabled: false,
      href: hrefOut,
      tooltip: tooltipLinesBase.join('\n'),
      isPendingRefresh: false
    };
  }

  if (isTyListingPendingStatus(d)) {
    tooltipLinesBase.push(tyUrl ? `Bağlantı: ${tyUrl}` : 'Trendyol ürün bağlantısı yok');
    tooltipLinesBase.push('Onay sürecinde. Tıklayınca Trendyol’daki güncel durum alınır (Firestore yazılır).');
    return {
      tableVariant: 'pending',
      label: 'Onayda',
      greenLook: false,
      disabled: false,
      href: tyUrl || '',
      tooltip: tooltipLinesBase.join('\n'),
      isPendingRefresh: true
    };
  }

  const greenLookFallback = primUrl || (Boolean(tyUrl) && matchedOk);

  const tooltipClosed = [...tooltipLinesBase];
  tooltipClosed.push(tyUrl ? `Bağlantı: ${tyUrl}` : 'Trendyol ürün bağlantısı yok');

  return {
    tableVariant: 'closed',
    label: 'Trendyol',
    greenLook: false,
    disabled: !(tyUrl && greenLookFallback),
    href: tyUrl || '',
    tooltip: tooltipClosed.join('\n'),
    isPendingRefresh: false
  };
}
